import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  Subject,
  Subscription,
  debounceTime,
  distinctUntilChanged,
  merge,
} from 'rxjs';
import {
  TableSettings,
  TableSpacing,
} from 'src/app/components/pagination/pagination.component';
import {
  ApiInvoice,
  ApiInvoiceAutoPayStatus,
  ApiInvoicesRecentResponse,
  DesktopInvoice,
  DesktopInvoiceDetails,
  DesktopInvoicesRecentResponse,
  GetRecentInvoicesRequest,
  GetScheduledInvoicesRequest,
  IgnoredApiInvoiceDetails,
  InvoicesService,
  ValidateApiInvoiceRecordResponse,
} from 'src/app/services/invoices.service';
import {
  SalesRep,
  SalesRepsService,
} from 'src/app/services/sales-reps.service';
import { ToastService } from 'src/app/services/toast.service';

type Sort = {
  field: string;
  direction: 'ASC' | 'DESC';
};

@Component({
  selector: 'app-invoices-list',
  templateUrl: './invoices-list.component.html',
  styleUrl: './invoices-list.component.scss',
})
export class InvoicesListComponent implements OnDestroy {
  @ViewChild('deleteDesktopInvoiceDialog')
  deleteDesktopInvoiceDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('deleteApiInvoiceDialog')
  deleteApiInvoiceDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('uploadApiInvoicesDialog')
  uploadApiInvoicesDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('uploadJobLogsDialog')
  uploadJobLogsDialog!: ElementRef<HTMLDialogElement>;
  @ViewChild('autoProcessAllPendingDialog')
  autoProcessAllPendingDialog!: ElementRef<HTMLDialogElement>;

  @ViewChild('apiTransactionsFileInput')
  apiTransactionsFileInput!: ElementRef<HTMLInputElement>;
  @ViewChild('jobLogsFileInput')
  jobLogsFileInput!: ElementRef<HTMLInputElement>;

  currentTab: 'desktop' | 'api' | 'scheduled' = 'api';

  pageSize = 10;
  currentPage = 1;
  totalPages = 1;

  tableSpacing: TableSpacing = 'normal';

  accountSearchQuery = '';
  companySearchQuery = '';

  monthFilter: string = '';
  monthFilterOptions: { label: string; value: string }[] = [];

  downloadMonth: string = '';
  downloadingReceipts = false;

  salesReps: SalesRep[] = [];
  apiInvoiceSalesRepFilterId: number | null = null;
  apiInvoiceSortBy: 'account_number' | 'company_name' = 'company_name';
  apiInvoiceSortDirection: 'ASC' | 'DESC' = 'ASC';

  // Subjects for debounced search
  private accountSearchSubject = new Subject<string>();
  private companySearchSubject = new Subject<string>();
  private searchSubscription?: Subscription;

  includePending = true;
  includeExpired = true;
  includeComplete = true;

  scheduledIncludePending = true;
  scheduledIncludeFailed = true;
  scheduledIncludeCompleted = true;

  desktopInvoices?: DesktopInvoicesRecentResponse[];
  fetchingDesktopInvoices = false;
  errorFetchingDesktopInvoices = '';

  apiInvoices?: ApiInvoicesRecentResponse[];
  fetchingApiInvoices = false;
  errorFetchingApiInvoices = '';

  scheduledInvoices?: ApiInvoicesRecentResponse[];
  fetchingScheduledInvoices = false;
  errorFetchingScheduledInvoices = '';

  selectedApiInvoiceIds = new Set<string>();
  settingSelectedApiInvoicesToAutoPay = false;
  errorSettingSelectedApiInvoicesToAutoPay = '';

  pendingUnqueuedCount?: number;
  fetchingPendingUnqueuedCount = false;
  autoProcessAllPendingDialogOpen = false;
  queueingAllPendingApiInvoices = false;
  errorQueueingAllPendingApiInvoices = '';

  scheduledInvoiceForActionsMenu?: ApiInvoice;
  scheduledInvoiceActionsMenuOpen = false;
  removingFromScheduledInvoices = false;
  errorRemovingFromScheduledInvoices = '';

  scheduledRefreshTimer?: NodeJS.Timeout;
  scheduledRefreshInterval = 60 * 1000; // 60 seconds
  maxScheduledRefreshCount = 100;
  currentScheduledRefreshCount = 0;

  apiTransactionsFile?: File;
  validatingApiTransactionsFile = false;
  apiFileValidationResponse?: ValidateApiInvoiceRecordResponse[];
  ignoredApiInvoices: IgnoredApiInvoiceDetails[] = [];
  errorValidatingApiTransactionsFile = '';
  uploadingApiInvoices = false;
  errorUploadingApiInvoices = '';

  currentSort?: Sort;

  desktopInvoiceToDelete?: DesktopInvoiceDetails;
  fetchingDesktopInvoiceToDelete = false;
  errorFetchingDesktopInvoiceToDelete = '';
  deletingDesktopInvoice = false;
  errorDeletingDesktopInvoice = '';

  apiInvoiceToDelete?: ApiInvoice;
  deletingApiInvoice = false;
  errorDeletingApiInvoice = '';

  selectedJobLogFiles?: File[];
  uploadingJobLogs = false;
  errorUploadingJobLogs = '';
  uploadJobLogsResult?: {
    files: {
      filename: string;
      success: boolean;
      error?: string;
      job_count: number;
    }[];
  };

  constructor(
    private invoicesService: InvoicesService,
    private salesRepsService: SalesRepsService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    // if query params has tab set, use it to set the current tab
    this.route.queryParams.subscribe((params) => {
      const tab = params['tab'];
      if (tab === 'desktop' || tab === 'api' || tab === 'scheduled') {
        this.switchTab(tab as 'desktop' | 'api' | 'scheduled');
      }
    });

    this.loadSettings();
    this.setupDebouncedSearch();
    this.fetchSalesReps();
    this.fetchRecentDesktopInvoices();
    this.fetchAvailableApiInvoiceTransactionMonths();
    this.fetchScheduledInvoices();
  }

  ngOnDestroy() {
    this.searchSubscription?.unsubscribe();
    this.accountSearchSubject.complete();
    this.companySearchSubject.complete();
    if (this.scheduledRefreshTimer) {
      clearTimeout(this.scheduledRefreshTimer);
      this.scheduledRefreshTimer = undefined;
    }
  }

  formatSalesReps(reps: SalesRep[] | undefined): string {
    if (!reps?.length) {
      return '';
    }
    return reps.map((r) => r.name).join(', ');
  }

  setupDebouncedSearch() {
    const debouncedAccountSearch = this.accountSearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    );

    const debouncedCompanySearch = this.companySearchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged()
    );

    this.searchSubscription = merge(
      debouncedAccountSearch,
      debouncedCompanySearch
    ).subscribe(() => {
      this.searchInvoices();
    });
  }

  onAccountSearchChange(value: string) {
    this.accountSearchQuery = value;
    this.accountSearchSubject.next(value);
  }

  onCompanySearchChange(value: string) {
    this.companySearchQuery = value;
    this.companySearchSubject.next(value);
  }

  loadSettings() {
    const settings = localStorage.getItem('invoiceTableSettings');
    try {
      if (settings) {
        const parsedSettings = JSON.parse(settings);
        this.tableSpacing = parsedSettings.spacing;
        this.pageSize = parsedSettings.pageSize;
      }
    } catch (err) {
      console.error(err);
      this.tableSpacing = 'normal';
      this.pageSize = 10;
    }
  }

  fetchRecentDesktopInvoices() {
    this.fetchingDesktopInvoices = true;
    this.errorFetchingDesktopInvoices = '';
    const request: GetRecentInvoicesRequest = {
      page_size: this.pageSize,
      page_number: this.currentPage,
      include_pending: this.includePending,
      include_expired: this.includeExpired,
      include_completed: this.includeComplete,
    };
    if (this.accountSearchQuery) {
      request.account_number = this.accountSearchQuery;
    }
    if (this.companySearchQuery) {
      request.company_name = this.companySearchQuery;
    }
    this.invoicesService.getRecentDesktopInvoices(request).subscribe({
      next: (res) => {
        this.fetchingDesktopInvoices = false;
        console.log('Desktop Invoices: ', res);
        this.desktopInvoices = res;
      },
      error: (err) => {
        this.fetchingDesktopInvoices = false;
        console.error(err);
        this.errorFetchingDesktopInvoices = 'Error fetching invoices';
      },
    });
  }

  fetchAvailableApiInvoiceTransactionMonths() {
    this.invoicesService.getAvailableApiInvoiceTransactionMonths().subscribe({
      next: (res) => {
        const months = res.transaction_yyyymm ?? [];
        this.monthFilterOptions = months.map((m) => ({
          value: m,
          label: this.getDisplayTransactionMonth(m).toLocaleDateString(
            'en-US',
            { month: 'long', year: 'numeric' }
          ),
        }));
        if (months.length > 0) {
          this.monthFilter = months[0];
          // autoselect the latest month for receipt download (months are DESC)
          this.downloadMonth = months[0];
        }
        this.fetchRecentApiInvoices();
      },
      error: (err) => {
        console.error(err);
        this.fetchRecentApiInvoices();
      },
    });
  }

  fetchRecentApiInvoices() {
    this.fetchingApiInvoices = true;
    this.errorFetchingApiInvoices = '';
    const request: GetRecentInvoicesRequest = {
      page_size: this.pageSize,
      page_number: this.currentPage,
      include_pending: this.includePending,
      include_expired: this.includeExpired,
      include_completed: this.includeComplete,
      sort_by: this.apiInvoiceSortBy,
      sort_direction: this.apiInvoiceSortDirection,
    };
    if (this.accountSearchQuery) {
      request.account_number = this.accountSearchQuery;
    }
    if (this.companySearchQuery) {
      request.company_name = this.companySearchQuery;
    }
    if (this.apiInvoiceSalesRepFilterId != null) {
      request.sales_rep_id = this.apiInvoiceSalesRepFilterId;
    }
    if (this.monthFilter) {
      request.transaction_yyyymm = this.monthFilter;
    }
    this.invoicesService.getRecentApiInvoices(request).subscribe({
      next: (res) => {
        this.fetchingApiInvoices = false;
        console.log('API Invoices: ', res);
        this.apiInvoices = res;
      },
      error: (err) => {
        this.fetchingApiInvoices = false;
        console.error(err);
        this.errorFetchingApiInvoices = 'Error fetching invoices';
      },
    });
  }

  fetchScheduledInvoices() {
    this.fetchingScheduledInvoices = true;
    this.errorFetchingScheduledInvoices = '';
    const request: GetScheduledInvoicesRequest = {
      page_size: this.pageSize,
      page_number: this.currentPage,
      include_pending: this.scheduledIncludePending,
      include_failed: this.scheduledIncludeFailed,
      include_completed: this.scheduledIncludeCompleted,
    };
    if (this.accountSearchQuery) {
      request.account_number = this.accountSearchQuery;
    }
    if (this.companySearchQuery) {
      request.company_name = this.companySearchQuery;
    }
    this.invoicesService.getScheduledInvoices(request).subscribe({
      next: (res) => {
        this.fetchingScheduledInvoices = false;
        console.log('Scheduled Invoices: ', res);
        this.scheduledInvoices = res;
      },
      error: (err) => {
        this.fetchingScheduledInvoices = false;
        console.error(err);
        this.errorFetchingScheduledInvoices =
          'Error fetching scheduled invoices';
      },
    });
  }

  fetchSalesReps() {
    this.salesRepsService.getAllSalesReps().subscribe({
      next: (reps) => {
        this.salesReps = reps;
      },
      error: (err) => {
        console.error(err);
      },
    });
  }

  onScheduledFiltersChange() {
    this.fetchScheduledInvoices();
  }

  switchTab(tab: 'desktop' | 'api' | 'scheduled') {
    this.currentTab = tab;
    // add tab to query params
    this.router.navigate([], {
      queryParams: { tab: tab },
    });

    if (this.scheduledRefreshTimer) {
      clearInterval(this.scheduledRefreshTimer);
      this.scheduledRefreshTimer = undefined;
    }

    // Fetch data for the selected tab if needed
    if (tab === 'scheduled') {
      this.fetchScheduledInvoices();
      this.scheduleRefresh();
    }
  }

  scheduleRefresh() {
    this.scheduledRefreshTimer = setTimeout(() => {
      this.currentScheduledRefreshCount++;
      this.scheduledRefreshInterval = Math.min(
        this.scheduledRefreshInterval * 1.1,
        30 * 60 * 1000 // 30 minutes max
      );
      if (this.currentScheduledRefreshCount >= this.maxScheduledRefreshCount) {
        clearTimeout(this.scheduledRefreshTimer);
        this.scheduledRefreshTimer = undefined;
        return;
      }
      this.fetchScheduledInvoices();
      this.scheduleRefresh();
    }, this.scheduledRefreshInterval);
  }

  fetchDesktopInvoiceDetails(invoiceId: string) {
    this.invoicesService.getInvoiceDetails(invoiceId).subscribe({
      next: (res) => {
        console.log('Invoice details: ', res);
        this.desktopInvoiceToDelete = res.invoice_details;
      },
      error: (err) => {
        console.error(err);
        this.errorFetchingDesktopInvoiceToDelete = 'Error fetching invoice';
      },
    });
  }

  searchInvoices() {
    this.fetchingDesktopInvoices = true;
    this.fetchingApiInvoices = true;
    this.fetchRecentDesktopInvoices();
    this.fetchRecentApiInvoices();
    if (this.currentTab === 'scheduled') {
      this.fetchScheduledInvoices();
    }
  }

  clearSearch() {
    this.accountSearchQuery = '';
    this.companySearchQuery = '';
    this.apiInvoiceSalesRepFilterId = null;
    this.monthFilter = '';
    this.fetchRecentDesktopInvoices();
    this.fetchRecentApiInvoices();
  }

  onApiSalesRepFilterChange() {
    this.fetchRecentApiInvoices();
  }

  onMonthFilterChange() {
    this.fetchRecentApiInvoices();
  }

  get downloadMonthLabel(): string {
    const opt = this.monthFilterOptions.find(
      (o) => o.value === this.downloadMonth
    );
    return opt?.label ?? 'Select month';
  }

  selectDownloadMonth(month: string) {
    this.downloadMonth = month;
  }

  downloadReceipts() {
    if (!this.downloadMonth || this.downloadingReceipts) {
      return;
    }

    this.downloadingReceipts = true;
    this.invoicesService
      .downloadApiInvoiceReceiptsByMonth(this.downloadMonth)
      .subscribe({
        next: (blob) => {
          this.downloadingReceipts = false;
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement('a');
          anchor.href = url;
          anchor.download = `api-invoice-receipts-${this.downloadMonth}.zip`;
          anchor.click();
          window.URL.revokeObjectURL(url);
        },
        error: async (err) => {
          this.downloadingReceipts = false;
          console.error(err);
          let message = 'Error downloading receipts';
          // error body is a blob since responseType is blob
          if (err?.error instanceof Blob) {
            try {
              const text = await err.error.text();
              const parsed = JSON.parse(text);
              message = parsed?.error || message;
            } catch {
              // keep default message
            }
          }
          this.toastService.addToast('error', message);
        },
      });
  }

  onApiInvoiceSortChange(field: 'account_number' | 'company_name') {
    if (this.apiInvoiceSortBy === field) {
      this.apiInvoiceSortDirection =
        this.apiInvoiceSortDirection === 'ASC' ? 'DESC' : 'ASC';
    }
    this.apiInvoiceSortBy = field;

    this.currentPage = 1;
    this.fetchRecentApiInvoices();
  }

  changePage(page: number) {
    this.currentPage = page;
    this.fetchRecentDesktopInvoices();
    this.fetchRecentApiInvoices();
    if (this.currentTab === 'scheduled') {
      this.fetchScheduledInvoices();
    }
  }

  handleSettingsChanged(settings: TableSettings) {
    this.tableSpacing = settings.spacing;
    this.pageSize = Number(settings.pageSize);
    this.fetchRecentDesktopInvoices();
    this.fetchRecentApiInvoices();
    if (this.currentTab === 'scheduled') {
      this.fetchScheduledInvoices();
    }
    const newSettings = {
      pageSize: Number(settings.pageSize),
      spacing: settings.spacing,
    };
    localStorage.setItem('invoiceTableSettings', JSON.stringify(newSettings));
  }

  invoiceStatus(invoice: DesktopInvoice | ApiInvoice) {
    if (invoice.completed) {
      if (invoice.status === 'refunded') {
        return 'Refunded';
      }
      if (invoice.status === 'voided') {
        return 'Voided';
      }
      return 'Completed';
    }
    if ((invoice as ApiInvoice).auto_pay_enabled) {
      if ((invoice as ApiInvoice).auto_pay_status === 'pending') {
        return 'Queued';
      }
      if ((invoice as ApiInvoice).auto_pay_status === 'processing') {
        return 'Processing';
      }
      if ((invoice as ApiInvoice).auto_pay_status === 'failed') {
        return 'Auto Pay Failed';
      }
      if ((invoice as ApiInvoice).auto_pay_status === 'completed') {
        return 'Completed';
      }
    }
    if (new Date() < new Date(invoice.expiration)) {
      return 'Pending';
    }
    return 'Expired';
  }

  scheduledInvoiceStatus(invoice: ApiInvoice): ApiInvoiceAutoPayStatus | null {
    // an invoice paid outside of the auto pay workflow is completed, even if
    // auto pay previously failed or is still queued
    if (invoice.completed) {
      return 'completed';
    }
    return invoice.auto_pay_status ?? null;
  }

  browseToInvoice(
    invoiceId: string,
    tenantId: string,
    type: 'desktop' | 'api'
  ) {
    const path = type === 'desktop' ? 'invoice' : 'api-invoice';
    this.router.navigate(['/admin/tenant', tenantId, path, invoiceId]);
  }

  isApiInvoiceSelected(id: string): boolean {
    return this.selectedApiInvoiceIds.has(id);
  }

  toggleApiInvoiceSelection(ev: Event, invoice: ApiInvoice) {
    ev.stopPropagation();

    const id = invoice.id;
    if (this.selectedApiInvoiceIds.has(id)) {
      this.selectedApiInvoiceIds.delete(id);
    } else {
      this.selectedApiInvoiceIds.add(id);
    }
  }

  openAutoProcessAllPendingDialog() {
    this.autoProcessAllPendingDialogOpen = true;
    this.pendingUnqueuedCount = undefined;
    this.fetchingPendingUnqueuedCount = true;
    this.invoicesService.getPendingUnqueuedApiInvoicesCount().subscribe({
      next: (res) => {
        this.fetchingPendingUnqueuedCount = false;
        this.pendingUnqueuedCount = res.count;
        this.autoProcessAllPendingDialog.nativeElement.showModal();
      },
      error: (err) => {
        this.fetchingPendingUnqueuedCount = false;
        this.autoProcessAllPendingDialogOpen = false;
        this.toastService.addToast('error', 'Failed to load pending count');
      },
    });
  }

  closeAutoProcessAllPendingDialog() {
    this.autoProcessAllPendingDialog.nativeElement.close();
    this.autoProcessAllPendingDialogOpen = false;
    this.pendingUnqueuedCount = undefined;
    this.errorQueueingAllPendingApiInvoices = '';
  }

  confirmAutoProcessAllPending() {
    this.queueingAllPendingApiInvoices = true;
    this.errorQueueingAllPendingApiInvoices = '';
    this.invoicesService.queueAllPendingApiInvoices().subscribe({
      next: (res) => {
        this.queueingAllPendingApiInvoices = false;
        this.closeAutoProcessAllPendingDialog();
        this.fetchRecentApiInvoices();
        this.fetchScheduledInvoices();
        this.toastService.addToast(
          'success',
          `${res.api_invoices.length} invoice(s) added to auto processing queue`
        );
      },
      error: (err) => {
        this.queueingAllPendingApiInvoices = false;
        this.errorQueueingAllPendingApiInvoices =
          err?.error?.error || 'Failed to queue invoices';
      },
    });
  }

  setSelectedApiInvoicesToAutoPay() {
    if (this.selectedApiInvoiceIds.size === 0) {
      return;
    }

    this.settingSelectedApiInvoicesToAutoPay = true;
    this.errorSettingSelectedApiInvoicesToAutoPay = '';

    const ids = [...this.selectedApiInvoiceIds];

    this.invoicesService.queueAutoPayForApiInvoices(ids).subscribe({
      next: () => {
        this.settingSelectedApiInvoicesToAutoPay = false;
        this.selectedApiInvoiceIds.clear();
        // Refresh lists so newly queued invoices appear appropriately
        this.fetchRecentApiInvoices();
        this.fetchScheduledInvoices();
        this.toastService.addToast(
          'success',
          `Auto processing started for ${ids.length} invoice(s)`
        );
      },
      error: (err) => {
        console.error(err);
        this.settingSelectedApiInvoicesToAutoPay = false;
        this.errorSettingSelectedApiInvoicesToAutoPay =
          err.error?.error ||
          'Unexpected error occurred while auto processing invoices. Please try again.';
      },
    });
  }

  openScheduledInvoiceActionsMenu(ev: Event, invoice: ApiInvoice) {
    ev.stopPropagation();
    if (this.scheduledInvoiceForActionsMenu?.id === invoice.id) {
      this.closeScheduledInvoiceActionsMenu();
      return;
    }
    this.scheduledInvoiceForActionsMenu = invoice;
    this.scheduledInvoiceActionsMenuOpen = true;
  }

  closeScheduledInvoiceActionsMenu() {
    this.scheduledInvoiceActionsMenuOpen = false;
    this.scheduledInvoiceForActionsMenu = undefined;
  }

  disableApiInvoiceAutoPay(invoice: ApiInvoice) {
    this.invoicesService.disableApiInvoiceAutoPay(invoice.id).subscribe({
      next: (res) => {
        console.log('Invoice removed from scheduled auto pay: ', res);
        this.closeScheduledInvoiceActionsMenu();
        this.fetchScheduledInvoices();
        this.fetchRecentApiInvoices();
        this.toastService.addToast(
          'success',
          'Invoice removed from scheduled auto pay'
        );
      },
      error: (err) => {
        console.error(err);
        this.errorRemovingFromScheduledInvoices =
          'Error removing invoice from scheduled auto pay';
      },
    });
  }

  promptDeleteDesktopInvoice(ev: Event, invoice: DesktopInvoice) {
    ev.stopPropagation();
    this.fetchingDesktopInvoiceToDelete = true;
    this.errorFetchingDesktopInvoiceToDelete = '';
    this.deleteDesktopInvoiceDialog.nativeElement.showModal();

    console.log('prompting to delete invoice: ', invoice);

    this.invoicesService.getInvoiceDetails(invoice.id).subscribe({
      next: (res) => {
        console.log('Invoice to delete details: ', res);
        this.fetchingDesktopInvoiceToDelete = false;
        this.desktopInvoiceToDelete = res.invoice_details;
      },
      error: (err) => {
        console.error(err);
        this.fetchingDesktopInvoiceToDelete = false;
        this.errorFetchingDesktopInvoiceToDelete =
          'Error fetching invoice details';
      },
    });
  }

  closeDeleteDesktopInvoiceDialog() {
    this.deleteDesktopInvoiceDialog.nativeElement.close();
    this.desktopInvoiceToDelete = undefined;
  }

  deleteDesktopInvoice(invoice: DesktopInvoiceDetails) {
    console.log('deleting desktop invoice: ', invoice);
    this.deletingDesktopInvoice = true;
    this.errorDeletingDesktopInvoice = '';

    this.invoicesService.deleteInvoice(invoice.desktop_invoice.id).subscribe({
      next: (res) => {
        console.log('Invoice deleted: ', res);
        this.deletingDesktopInvoice = false;
        this.errorDeletingDesktopInvoice = '';
        this.desktopInvoiceToDelete = undefined;

        // requery invoices to update the list
        this.fetchRecentDesktopInvoices();
        this.closeDeleteDesktopInvoiceDialog();

        this.toastService.addToast('success', 'Invoice deleted successfully');
      },
      error: (err) => {
        console.error(err);
        this.deletingDesktopInvoice = false;
        this.errorDeletingDesktopInvoice =
          'Unexpected error occurred. Please try again.';
      },
    });
  }

  promptDeleteApiInvoice(ev: Event, invoice: ApiInvoice) {
    ev.stopPropagation();
    this.apiInvoiceToDelete = invoice;
    this.deleteApiInvoiceDialog.nativeElement.showModal();

    console.log('prompting to delete invoice: ', invoice);
  }

  closeDeleteApiInvoiceDialog() {
    this.deleteApiInvoiceDialog.nativeElement.close();
    this.apiInvoiceToDelete = undefined;
  }

  deleteApiInvoice(invoice: ApiInvoice) {
    console.log('deleting api invoice: ', invoice);
    this.deletingApiInvoice = true;
    this.errorDeletingApiInvoice = '';

    this.invoicesService.deleteApiInvoice(invoice.id).subscribe({
      next: (res) => {
        console.log('Invoice deleted: ', res);
        this.deletingApiInvoice = false;
        this.errorDeletingApiInvoice = '';
        this.apiInvoiceToDelete = undefined;

        // requery invoices to update the list
        this.fetchRecentApiInvoices();
        this.closeDeleteApiInvoiceDialog();

        this.toastService.addToast('success', 'Invoice deleted successfully');
      },
      error: (err) => {
        console.error(err);
        this.deletingApiInvoice = false;
        this.errorDeletingApiInvoice =
          'Unexpected error occurred. Please try again.';
      },
    });
  }

  openUploadApiInvoicesDialog() {
    this.uploadApiInvoicesDialog.nativeElement.showModal();
  }

  closeUploadApiInvoicesDialog() {
    this.uploadApiInvoicesDialog.nativeElement.close();
    this.apiTransactionsFile = undefined;
    this.apiTransactionsFileInput.nativeElement.value = '';
    this.errorUploadingApiInvoices = '';
    this.uploadingApiInvoices = false;
    this.apiFileValidationResponse = undefined;
    this.ignoredApiInvoices = [];
    this.errorValidatingApiTransactionsFile = '';
    this.validatingApiTransactionsFile = false;
  }

  handleApiTransactionsFileChange(event: Event) {
    const file = (event.target as HTMLInputElement).files?.[0];
    console.log('file: ', file);
    this.apiTransactionsFile = file;

    this.apiFileValidationResponse = undefined;
    this.ignoredApiInvoices = [];
    this.errorValidatingApiTransactionsFile = '';
    this.validatingApiTransactionsFile = false;
  }

  get errorValidationResponses() {
    if (!this.apiFileValidationResponse) {
      return [];
    }

    return this.apiFileValidationResponse.filter((response) => response.error);
  }

  get ignoredValidationResponses() {
    if (!this.apiFileValidationResponse) {
      return [];
    }

    return this.apiFileValidationResponse.filter(
      (response) => !response.error && !!response.tenant?.ignore_api_invoices
    );
  }

  get zeroCostValidationResponses() {
    if (!this.apiFileValidationResponse) {
      return [];
    }

    return this.apiFileValidationResponse.filter(
      (response) =>
        !response.error &&
        !response.tenant?.ignore_api_invoices &&
        response.api_transaction_record.invoice_amount === 0
    );
  }

  get validValidationResponses() {
    if (!this.apiFileValidationResponse) {
      return [];
    }

    return this.apiFileValidationResponse.filter(
      (response) =>
        !response.error &&
        !response.tenant?.ignore_api_invoices &&
        response.api_transaction_record.invoice_amount > 0
    );
  }

  get hasInvalidJobLogFiles() {
    if (!this.selectedJobLogFiles) {
      return false;
    }
    return (
      this.selectedJobLogFiles.length > 0 &&
      this.selectedJobLogFiles.some(
        (file) => !this.isValidJobLogFileName(file.name)
      )
    );
  }

  getDisplayTransactionMonth(yyyymm: string): Date {
    return this.invoicesService.getDisplayTransactionMonth(yyyymm);
  }

  validateApiTransactionsFile() {
    console.log('validating api transactions file');
    console.log('api transactions file: ', this.apiTransactionsFile);

    if (!this.apiTransactionsFile) {
      this.errorValidatingApiTransactionsFile = 'No file selected';
      return;
    }

    this.validatingApiTransactionsFile = true;
    this.errorValidatingApiTransactionsFile = '';
    this.apiFileValidationResponse = undefined;
    this.ignoredApiInvoices = [];

    this.invoicesService
      .validateApiInvoices(this.apiTransactionsFile)
      .subscribe({
        next: (res) => {
          console.log('res: ', res);
          this.validatingApiTransactionsFile = false;
          this.errorValidatingApiTransactionsFile = '';
          this.apiFileValidationResponse = res;
        },
        error: (err) => {
          console.error(err);
          this.validatingApiTransactionsFile = false;
          this.errorValidatingApiTransactionsFile =
            err.error?.error || 'Unexpected error occurred. Please try again.';
        },
      });
  }

  uploadNewInvoices() {
    console.log('uploading new invoices');
    this.errorUploadingApiInvoices = '';

    if (!this.apiTransactionsFile) {
      this.errorUploadingApiInvoices = 'No file selected';
      return;
    }

    console.log('file: ', this.apiTransactionsFile);

    this.uploadingApiInvoices = true;
    this.invoicesService.uploadApiInvoices(this.apiTransactionsFile).subscribe({
      next: (res) => {
        console.log('res: ', res);
        this.uploadingApiInvoices = false;
        this.ignoredApiInvoices = res.ignored_invoices ?? [];

        if (res.validate_errors.length > 0) {
          this.apiTransactionsFile = undefined;
          this.apiTransactionsFileInput.nativeElement.value = '';
          this.errorValidatingApiTransactionsFile = '';
          this.validatingApiTransactionsFile = false;

          this.errorUploadingApiInvoices = 'Validation errors occurred';
          this.apiFileValidationResponse = res.validate_errors;
          return;
        }

        this.errorUploadingApiInvoices = '';
        this.apiTransactionsFile = undefined;
        this.apiTransactionsFileInput.nativeElement.value = '';
        this.apiFileValidationResponse = undefined;
        this.errorValidatingApiTransactionsFile = '';
        this.validatingApiTransactionsFile = false;
        this.closeUploadApiInvoicesDialog();
        this.fetchRecentApiInvoices();
        this.toastService.addToast('success', 'Invoices uploaded successfully');
      },
      error: (err) => {
        console.error(err);
        this.uploadingApiInvoices = false;
        this.errorUploadingApiInvoices =
          err.error?.error || 'Unexpected error occurred. Please try again.';
      },
    });
  }

  openUploadJobLogsDialog() {
    this.uploadJobLogsDialog.nativeElement.showModal();
  }

  closeUploadJobLogsDialog() {
    this.uploadJobLogsDialog.nativeElement.close();
    this.selectedJobLogFiles = undefined;
    this.uploadJobLogsResult = undefined;
    this.errorUploadingJobLogs = '';
    if (this.jobLogsFileInput) {
      this.jobLogsFileInput.nativeElement.value = '';
    }
  }

  handleJobLogsFileChange(event: Event) {
    const files = (event.target as HTMLInputElement).files;
    if (!files || files.length === 0) {
      this.selectedJobLogFiles = undefined;
      return;
    }

    this.selectedJobLogFiles = Array.from(files);
    this.uploadJobLogsResult = undefined;
    this.errorUploadingJobLogs = '';
  }

  isValidJobLogFileName(filename: string): boolean {
    // File name should be: 10-digit-account-number.log
    // Example: 7712345601.log
    const accountNumberRegex = /^(\d{10})\.log$/i;
    return accountNumberRegex.test(filename);
  }

  uploadJobLogs() {
    console.log('uploading job logs');
    this.errorUploadingJobLogs = '';

    if (!this.selectedJobLogFiles || this.selectedJobLogFiles.length === 0) {
      this.errorUploadingJobLogs = 'No files selected';
      return;
    }

    if (
      this.selectedJobLogFiles.some(
        (file) => !this.isValidJobLogFileName(file.name)
      )
    ) {
      this.errorUploadingJobLogs =
        'Please fix validation errors before uploading';
      return;
    }

    this.uploadingJobLogs = true;
    this.uploadJobLogsResult = undefined;

    this.invoicesService.uploadJobLogs(this.selectedJobLogFiles).subscribe({
      next: (res) => {
        console.log('Job logs upload result: ', res);
        this.selectedJobLogFiles = undefined;
        this.uploadingJobLogs = false;
        this.errorUploadingJobLogs = '';
        this.uploadJobLogsResult = res;

        const successCount = res.files.filter((f) => f.success).length;
        const errorCount = res.files.filter((f) => !f.success).length;

        if (errorCount === 0) {
          this.toastService.addToast(
            'success',
            `Successfully uploaded ${successCount} job log file(s)`
          );
        } else {
          this.toastService.addToast(
            'warning',
            `Uploaded ${successCount} file(s) with ${errorCount} error(s)`
          );
        }
      },
      error: (err) => {
        console.error(err);
        this.uploadingJobLogs = false;
        this.errorUploadingJobLogs =
          err.error?.error || 'Unexpected error occurred. Please try again.';
      },
    });
  }

  getSuccessfulFileCount(): number {
    if (!this.uploadJobLogsResult) {
      return 0;
    }
    return this.uploadJobLogsResult.files.filter((f) => f.success).length;
  }

  getTotalFileCount(): number {
    if (!this.uploadJobLogsResult) {
      return 0;
    }
    return this.uploadJobLogsResult.files.length;
  }

  allFilesSuccessful(): boolean {
    if (!this.uploadJobLogsResult) {
      return false;
    }
    return (
      this.uploadJobLogsResult.files.filter((f) => f.success).length ===
      this.uploadJobLogsResult.files.length
    );
  }
}
